import React from 'react';
import { reportClientError } from '../lib/reportClientError';
import './ErrorBoundary.css';

/**
 * ErrorBoundary — the app's last line of defence.
 *
 * Without one, any render-time throw unmounts the whole tree and leaves a blank
 * white page: no message, nothing to report, and no way for a user to tell us
 * anything more useful than "it's white". That is exactly how the Phase A
 * mobile outage presented, and why it cost a rollback to diagnose.
 *
 * So this does two jobs. It shows the user something honest and actionable, and
 * it posts the error to /api/client-errors where it shows up in admin App Logs.
 *
 * Deliberately plain: no hooks, no i18n, no api client, no icon library. Every
 * dependency is one more thing that can be broken at the moment this renders.
 * The copy is hard-coded English for the same reason — reaching into the
 * translation layer to apologise for a crash is a good way to crash again.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, showDetail: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    reportClientError('render', error, { componentStack: info?.componentStack });
  }

  render() {
    const { error, showDetail } = this.state;
    if (!error) return this.props.children;

    const detail = `${error.message || error}\n\n${error.stack || ''}`.trim();

    return (
      <div className="eb-wrap" role="alert">
        <div className="eb-card">
          <img src="/logo192.png" alt="" className="eb-logo" />
          <h1 className="eb-title">Something went wrong</h1>
          <p className="eb-body">
            Sorry — Ignite hit an error and couldn’t finish loading this screen.
            Your data is safe. Reloading usually fixes it.
          </p>

          <div className="eb-actions">
            {/* A hard location assignment, not react-router: the router is part
                of the tree that just failed and may not be able to navigate. */}
            <button className="eb-btn eb-btn-primary" onClick={() => window.location.reload()}>
              Reload
            </button>
            <button className="eb-btn" onClick={() => { window.location.href = '/'; }}>
              Go to Home
            </button>
          </div>

          <button
            className="eb-toggle"
            aria-expanded={showDetail}
            onClick={() => this.setState(s => ({ showDetail: !s.showDetail }))}>
            {showDetail ? 'Hide details' : 'Show details'}
          </button>

          {showDetail && (
            <>
              {/* Shown so a user can read or screenshot the actual error rather
                  than describing a blank page. It has already been reported. */}
              <pre className="eb-detail">{detail}</pre>
              <button
                className="eb-btn eb-copy"
                onClick={() => navigator.clipboard?.writeText(detail).catch(() => {})}>
                Copy error
              </button>
            </>
          )}
        </div>
      </div>
    );
  }
}
